<template>
  <div class="add-field">
    <div class="add-field__content">
      <div>
        <FormInput
          ref="fieldNameInput"
          :class="{ 'has-error': nameError }"
          :error="nameError"
          :label="$t('evaluation-grid.management.field-name')"
          name="name"
          type="text"
          hasGrayBackground
          is-required
          v-model="name"
        />
        <div class="add-field__field add-field__description">
          <FormInput
            ref="fieldDescriptionInput"
            :label="$t('evaluation-grid.management.field-description')"
            name="description"
            hasGrayBackground
            is-required
            is-textarea
            v-model="description"
          />
          <div class="add-field__keywords-subtext">
            {{ $t('evaluation-grid.management.field-description-subtext') }}
          </div>
        </div>
        <BaseSelect
          class="add-field__field"
          name="type"
          :label="$t('evaluation-grid.management.field-type')"
          v-model="type"
          :items="fieldTypes"
          item-text="name"
          item-value="value"
          @change="onTypeChange"
        />
        <div class="add-field__field" v-if="type === 'SELECT'">
          <FormLabel>{{ $t('evaluation-grid.management.field-values') }}</FormLabel>
          <BaseMultiTagCreation
            :values="possibleValues.map((possibleValue) => ({ value: possibleValue }))"
            @values-changed="updatePossibleValues"
          />
        </div>
        <div class="add-field__field add-field__llm-extraction">
          <div>
            <FormLabel>
              {{ $t('evaluation-grid.management.field-llm-extraction') }}
              <BaseIcon name="ai" class="ai-icon" />
            </FormLabel>
            <div class="add-field__keywords-subtext">
              {{ $t('evaluation-grid.management.field-llm-extraction-subtext') }}
            </div>
          </div>
          <BaseToggle
            toggleKey="llmExtractionToggle"
            :value="useInLlmExtraction"
            @change="handleToggle"
            :disabled="isToggleDisabled"
            :tooltipText="isToggleDisabled ? $t('evaluation-grid.management.field-llm-extraction-tooltip') : ''"
          />
        </div>
      </div>
      <div class="add-field__actions">
        <BaseButton is-outlined @click.native="close">{{ $t('actions.cancel') }}</BaseButton>
        <BaseButton @click.native="save" :disabled="isDisabled">
          <span>{{ $t('actions.save') }}</span>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMultiTagCreation from '@/components/base/BaseMultiTagCreation.vue';
import BaseToggle from '@/components/base/BaseToggle';
import BaseSelect from '@/components/base/BaseSelect';
import FormInput from '@/components/form/FormInput';
import FormLabel from '@/components/form/FormLabel';

export default {
  name: 'EvaluationGridFieldForm',
  components: {
    FormInput,
    FormLabel,
    BaseSelect,
    BaseButton,
    BaseIcon,
    BaseToggle,
    BaseMultiTagCreation,
  },
  props: {
    initialData: {
      type: Object,
      required: false,
    },
    evaluationGridFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      name: '',
      description: '',
      type: 'TEXT',
      possibleValues: [],
      useInLlmExtraction: true,
      fieldTypes: [
        {
          name: `${this.$t('evaluation-grid.management.free-text')}`,
          value: 'TEXT',
          icon: 'mdi-text',
        },
        {
          name: `${this.$t('evaluation-grid.management.number')}`,
          value: 'NUMBER',
          icon: 'mdi-text',
        },
        {
          name: `${this.$t('evaluation-grid.management.multi-select')}`,
          value: 'SELECT',
          icon: 'mdi-format-list-bulleted',
        },
        {
          name: `${this.$t('evaluation-grid.management.image')}`,
          value: 'IMAGE',
          icon: 'mdi-text',
        },
      ],
      isToggleDisabled: false,
    };
  },
  computed: {
    nameError() {
      const newKey = this.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');
      const currentFieldId = this.initialData && this.initialData.id;
      const nameExist = this.evaluationGridFields.some((field) => field.key === newKey && field.id !== currentFieldId);
      return nameExist ? this.$t('evaluation-grid.add-field.field-exist') : undefined;
    },
    isDisabled() {
      return (
        this.name.trim() === '' ||
        this.description.trim() === '' ||
        this.nameError !== undefined ||
        (this.type === 'SELECT' && (!this.possibleValues.length || this.type.trim() === '')) ||
        !this.isDirty
      );
    },
    isDirty() {
      const isEdit = !!this.initialData;
      if (!isEdit) {
        return true;
      }

      const hasChoicesChanged =
        this.initialData.type !== this.type ||
        (this.type === 'SELECT' &&
          !(
            this.initialData.choices.length === this.possibleValues.length &&
            this.initialData.choices.every((possibleValue) => this.possibleValues.includes(possibleValue))
          ));
      return (
        this.initialData.name !== this.name ||
        this.initialData.description !== this.description ||
        this.initialData.useInExtraction !== this.useInLlmExtraction ||
        hasChoicesChanged
      );
    },
  },
  methods: {
    save() {
      const lastOrderValue =
        this.evaluationGridFields.length > 0
          ? this.evaluationGridFields[this.evaluationGridFields.length - 1].order
          : 0;
      const nextOrderValue = lastOrderValue + 1;
      const nameFormattedToKey = this.name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
      const newField = {
        id: this.initialData && this.initialData.id ? this.initialData.id : undefined,
        order: this.initialData && this.initialData.order ? this.initialData.order : nextOrderValue,
        key: nameFormattedToKey,
        name: this.name.trim(),
        description: this.description.trim(),
        type: this.type,
        choices: this.possibleValues,
        priority: this.initialData && this.initialData.priority ? this.initialData.priority : 'LOW',
        keywords: [], //todo: cleanup once the BE no longer expects this field
        useInExtraction: this.useInLlmExtraction,
      };
      this.$emit('save', newField);
      this.close();
    },
    close() {
      this.resetForm();
      this.$emit('close');
    },
    resetForm() {
      this.setFormData(undefined);
    },
    setFormData(data) {
      this.name = data && data.name ? data.name : '';
      this.description = data && data.description ? data.description : '';
      this.type = data && data.type ? data.type : 'TEXT';
      this.possibleValues = data && data.choices ? data.choices : [];
      this.useInLlmExtraction = data && data.useInExtraction !== undefined ? data.useInExtraction : true;

      if (this.type === 'IMAGE' || this.type === 'NUMBER') {
        this.useInLlmExtraction = false;
        this.isToggleDisabled = true;
      } else {
        this.isToggleDisabled = false;
      }
    },
    updatePossibleValues(values) {
      this.possibleValues = values.map((item) => item.value);
    },
    onTypeChange(type) {
      this.type = type;
      this.possibleValues = [];

      if (type === 'IMAGE' || type === 'NUMBER') {
        this.isToggleDisabled = true;
        this.useInLlmExtraction = false;
      } else {
        this.isToggleDisabled = false;
        this.useInLlmExtraction = true;
      }
    },
    focusOnFirstInput() {
      const inputEl = this.$refs.nameInput.$el.querySelector('input');
      if (inputEl) inputEl.focus();
    },
    handleToggle(value) {
      this.useInLlmExtraction = value;
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(newValues) {
        this.setFormData(newValues);
      },
    },
  },
};
</script>

<style lang="scss">
.add-field {
  &__values-row {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  &__field {
    padding-bottom: 24px;
  }

  &__values-input-container {
    background-color: $color-neutral-pale;
    transition: all 200ms ease;
    border: 1px transparent solid;
    flex: 1;
    flex-wrap: wrap;
    min-height: 42px;
    border-radius: $border-radius-small;
    align-items: center;
    display: flex;
    padding: 4px 8px;

    &.focus {
      border: 1px solid $color-accent;
    }
  }

  &__values-input {
    width: 1px;
    margin-left: 4px;
    border: 0;
    cursor: text !important;
    outline: none;
  }

  &__valus-placeholer {
    color: $color-neutral;
    font-size: 16px;
  }

  &__keywords {
    &-subtext {
      color: $color-neutral;
      font-size: $font-size-small;
      margin-top: 4px;
    }
  }

  &__llm-extraction {
    display: flex;
    gap: 8px;

    .ai-icon {
      margin-left: 8px;
    }
  }

  &__add-value-btn {
    .base-button__element {
      border-radius: $border-radius-medium !important;
      height: 42px !important;
      padding: 8px 11px !important;
      .feather {
        margin-right: 0 !important;
      }
    }
  }

  &__actions {
    padding-top: 24px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .form-label {
    font-size: 14px;
    display: flex;
    align-items: center;
  }

  .base-select {
    .v-list-item__title {
      font-size: 14px;
      align-items: center;

      .v-icon {
        margin-top: -4px;
        font-size: 18px;
        margin-right: 4px;
      }
    }
  }

  &__description {
    .form-input {
      padding-bottom: 0 !important;
    }
  }
}
</style>
